export const formatTimestamp = (timestamp: Date) =>
  new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });

export const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase();

export const formatters = {
  formatDate: (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  },

  formatDateTime: (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  },

  formatRelativeTime: (date: Date | string): string => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return formatters.formatDate(date);
  },

  formatCurrency: (amount: number, currency = "USD"): string =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency
    }).format(amount),

  formatNumber: (num: number): string => new Intl.NumberFormat("en-US").format(num),

  formatCompactNumber: (num: number): string =>
    new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1
    }).format(num),

  formatMetricNumber: (
    num: number,
    options: {
      compact?: boolean;
      showDecimals?: boolean;
      prefix?: string;
      suffix?: string;
    } = {}
  ): string => {
    const { compact = true, showDecimals = false, prefix = "", suffix = "" } = options;

    if (num === 0) return `${prefix}0${suffix}`;

    let formattedNumber: string;

    if (compact) {
      // Enhanced compact formatting with better thresholds
      if (num >= 1000000000) {
        formattedNumber = `${(num / 1000000000).toFixed(showDecimals ? 1 : 0)}B`;
      } else if (num >= 1000000) {
        formattedNumber = `${(num / 1000000).toFixed(showDecimals ? 1 : 0)}M`;
      } else if (num >= 100000) {
        formattedNumber = `${(num / 1000).toFixed(showDecimals ? 0 : 0)}K`;
      } else if (num >= 10000) {
        formattedNumber = `${(num / 1000).toFixed(showDecimals ? 1 : 0)}K`;
      } else if (num >= 1000) {
        formattedNumber = `${(num / 1000).toFixed(1)}K`;
      } else {
        formattedNumber = num.toLocaleString("en-US");
      }
    } else {
      formattedNumber = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: showDecimals ? 2 : 0
      }).format(num);
    }

    return `${prefix}${formattedNumber}${suffix}`;
  },

  formatPercentage: (num: number, decimals: number = 1): string => `${num.toFixed(decimals)}%`,

  formatMetricValue: (num: number, type: "default" | "compact" | "full" = "compact"): string => {
    switch (type) {
      case "full":
        return formatters.formatNumber(num);
      case "compact":
        return formatters.formatMetricNumber(num, { compact: true, showDecimals: true });
      default:
        return formatters.formatMetricNumber(num, { compact: true });
    }
  },

  truncateText: (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  },

  formatFileSize: (bytes: number): string => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / 1024 ** i) * 100) / 100} ${sizes[i]}`;
  }
};
